import type { Invoice, RegionId } from '@snootsvet/shared'
import { formatInterval } from '@snootsvet/shared'

import { Badge } from '@/frontend/components/ui/badge'
import { FleaWormingPlanBadge } from '@/frontend/features/patient/patient-membership-details'

interface SubscriptionBadgesProps {
  status: string
  interval?: string | null
  intervalCount?: number | null
  hasFleaWormTickPlan: boolean
  memberRegion: RegionId
  invoices?: Invoice[]
  pendingUpdate?: boolean
}

/**
 * Compact status badges for a membership. Ordered most- to least-urgent so the
 * things staff need to act on read first.
 */
export function SubscriptionBadges({
  status,
  interval,
  intervalCount,
  hasFleaWormTickPlan,
  memberRegion,
  invoices,
  pendingUpdate,
}: SubscriptionBadgesProps) {
  const intervalLabel = interval ? formatInterval(interval, intervalCount) : null
  const label = intervalLabel ? `${intervalLabel} · ${statusLabel(status)}` : statusLabel(status)
  const overdueCount = invoices?.filter((inv) => inv.overdue).length ?? 0

  return (
    <div className="flex max-w-full shrink-0 flex-wrap items-center gap-1">
      <Badge variant={statusRole(status)} size="sm">
        {label}
      </Badge>
      {overdueCount > 0 && (
        <Badge variant="warning" size="sm">
          {overdueCount} overdue invoice{overdueCount > 1 ? 's' : ''}
        </Badge>
      )}
      {pendingUpdate && <Badge variant="pending">Pending upgrade</Badge>}
      <FleaWormingPlanBadge subscription={{ hasFleaWormTickPlan }} memberRegion={memberRegion} />
    </div>
  )
}

/** Human-readable label for a Stripe subscription status. */
function statusLabel(status: string): string {
  if (status === 'active') return 'Active'
  if (status === 'past_due') return 'Past Due'
  return status.charAt(0).toUpperCase() + status.slice(1).replaceAll('_', ' ')
}

/** Status role for a Stripe subscription status. */
function statusRole(status: string) {
  if (status === 'active') return 'success' as const
  if (status === 'past_due') return 'warning' as const
  return 'destructive' as const
}
